import { ApiErrorCode } from '@hprofits/api/common/errors';
import { ApiError, RequestPayloadValidationError, ResponsePayloadValidationError } from '@hprofits/api-client/errors';
import type { CampaignId } from '@hprofits/shared';
import { HpError } from '@hprofits/shared/errors';

import { t } from '@/i18n';
import type { ErrorWithDetails } from '@/utils/errors';

export type { ErrorWithDetails } from '@/utils/errors';
export { isErrorWithDetails } from '@/utils/errors';

export type PricingRuleErrorDetails = Readonly<{
  ruleId?: CampaignId;
  issueMessages?: ReadonlyArray<string>;
}>;

abstract class CampaignError extends HpError {}

type ValidationIssueLike = Readonly<{
  message?: unknown;
}>;

const isObjectLike = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const extractIssueMessages = (issues: ReadonlyArray<ValidationIssueLike>): Array<string> => {
  return [
    ...new Set(
      issues.flatMap((issue) => (typeof issue.message === 'string' && issue.message.length > 0 ? [issue.message] : [])),
    ),
  ];
};

const extractIssuesFromDetails = (details: unknown): ReadonlyArray<ValidationIssueLike> => {
  if (Array.isArray(details)) {
    return details.filter(isObjectLike);
  }

  if (isObjectLike(details) && Array.isArray(details.issues)) {
    return details.issues.filter(isObjectLike);
  }

  return [];
};

export const extractValidationErrorMessage = (error: unknown): string | null => {
  if (error instanceof RequestPayloadValidationError || error instanceof ResponsePayloadValidationError) {
    const messages = extractIssueMessages(error.validationError.issues);
    return messages.length > 0 ? messages.join(', ') : null;
  }

  if (error instanceof ApiError && error.errorCode === ApiErrorCode.REQUEST_BODY_VALIDATION) {
    const messages = extractIssueMessages(extractIssuesFromDetails(error.data));
    return messages.length > 0 ? messages.join(', ') : null;
  }

  return null;
};

/** Stack traces and logs are read in English; the user-facing wording comes from `getMessage()`. */
const UNKNOWN_ERROR_LOG_MESSAGE = 'Unknown error';

type PricingRuleErrorOptions = ErrorOptions &
  Readonly<{
    /**
     * Resolves the user-facing wording on read. Translating in the constructor pins the copy to the
     * locale the throw happened in and puts localized text into `Error.message`, which is what logs
     * and stack traces carry — `getTitle()` below resolves the same way, at read time.
     */
    localizedMessage?: () => string;
  }>;

export class PricingRuleError extends CampaignError implements ErrorWithDetails<PricingRuleErrorDetails> {
  declare readonly cause: Error | undefined;

  private readonly localizedMessage: (() => string) | undefined;

  constructor(
    message: string,
    readonly details?: PricingRuleErrorDetails,
    options?: PricingRuleErrorOptions,
  ) {
    super(message, options);
    this.localizedMessage = options?.localizedMessage;
  }

  static fromError(error: unknown, ruleId?: CampaignId): PricingRuleError {
    const ownMessage = extractValidationErrorMessage(error) ?? (error instanceof Error ? error.message : null);
    return new PricingRuleError(
      ownMessage ?? UNKNOWN_ERROR_LOG_MESSAGE,
      { ruleId },
      {
        cause: error,
        ...(ownMessage === null
          ? { localizedMessage: (): string => t('modules.campaigns.errors.pricingRule.unknown') }
          : {}),
      },
    );
  }

  public getTitle(): string {
    if (this.cause && this.cause instanceof ApiError) {
      switch (this.cause.errorCode) {
        case 'ENTITY_NOT_FOUND':
          return t('modules.campaigns.errors.pricingRule.notFound');
        case 'ENTITY_IN_USE':
          return t('modules.campaigns.errors.pricingRule.inUse');
        default:
        // do nothing, fall through to default
      }
    }
    return t('modules.campaigns.errors.pricingRule.title');
  }

  public getMessage(): string {
    return this.localizedMessage?.() ?? this.message;
  }
}
